import { MentionBlot } from 'quill-mention';
import Quill from 'quill';
import { defer, fromEvent, shareReplay } from 'rxjs';

export const loadQuillStyle$ = defer(() => {
  const quillStyles = 'quill.snow.css';

  const head = document.getElementsByTagName('head')[0];

  const quillLink = document.getElementById('quill-styles') as HTMLLinkElement;
  if (!quillLink) {
    const style = document.createElement('link');
    style.id = 'quill-styles';
    style.rel = 'stylesheet';
    style.href = `${quillStyles}`;

    head.appendChild(style);

    // Return an observable that emits when the stylesheet is loaded
    return fromEvent(style, 'load');
  } else {
    // If the stylesheet is already present, return an observable that emits immediately
    return new Promise<void>(resolve => resolve()).then();
  }
}).pipe(shareReplay({ bufferSize: 1, refCount: true }));

export class StyledMentionBlot extends MentionBlot {
  static override render(data: any) {
    const element = document.createElement('span');
    element.innerText = data.value;
    element.style.color = data.color;
    return element;
  }
}
StyledMentionBlot.blotName = 'styled-mention';

const EXTERNAL_URL_PATTERN = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;
let isQuillLinkSanitizerPatched = false;

function normalizeQuillLink(url: string): string {
  const trimmedUrl = url?.trim();
  if (!trimmedUrl) {
    return trimmedUrl;
  }

  if (
    EXTERNAL_URL_PATTERN.test(trimmedUrl) ||
    trimmedUrl.startsWith('/') ||
    trimmedUrl.startsWith('#') ||
    trimmedUrl.startsWith('?')
  ) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
}

export function patchQuillLinkSanitizer(): void {
  if (isQuillLinkSanitizerPatched) {
    return;
  }

  const linkFormat = Quill.import('formats/link') as {
    sanitize?: (url: string) => string;
  };

  if (typeof linkFormat?.sanitize !== 'function') {
    return;
  }

  const defaultSanitize = linkFormat.sanitize.bind(linkFormat);
  linkFormat.sanitize = (url: string) =>
    defaultSanitize(normalizeQuillLink(url));

  isQuillLinkSanitizerPatched = true;
}

export const DEFAULT_QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [{ header: 1 }, { header: 2 }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ color: [] as any[] }, { background: [] as any[] }],
    [{ align: [] as any[] }],
    ['clean'],
    ['link'],
  ],
};
